import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';

const Navbar = ({ isVisible = true, currentPage = 'home', onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [activeSection, setActiveSection] = useState(
    currentPage === 'contact'
      ? 'contact'
      : currentPage === 'about'
      ? 'about'
      : currentPage === 'services'
      ? 'services'
      : currentPage === 'customer-form'
      ? 'customer-form'
      : 'home'
  );

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // When at the very top, always show navbar
      if (currentScrollY <= 40) {
        setIsAtTop(true);
        setIsScrollingDown(false);
      } else {
        setIsAtTop(false);
        // Detect scroll direction
        if (currentScrollY > lastScrollY + 5 && currentScrollY > 70) {
          // Scrolling DOWN -> Hide
          setIsScrollingDown(true);
          setMobileMenuOpen(false); // close mobile menu if scrolling down
        } else if (currentScrollY < lastScrollY - 5) {
          // Scrolling UP -> Show
          setIsScrollingDown(false);
        }
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll spy to highlight active section on scroll
  useEffect(() => {
    if (currentPage === 'contact' || currentPage === 'about' || currentPage === 'services' || currentPage === 'life-insurance' || currentPage === 'customer-form') {
      setActiveSection(currentPage);
      return;
    }

    const handleScrollSpy = () => {
      const scrollPosition = window.scrollY + 160;

      const sections = [
        { id: 'contact', name: 'contact' },
        { id: 'solutions', name: 'solutions' },
        { id: 'about', name: 'about' },
        { id: 'home', name: 'home' },
      ];

      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          if (scrollPosition >= top) {
            setActiveSection(section.name);
            return;
          }
        }
      }
      setActiveSection('home');
    };

    window.addEventListener('scroll', handleScrollSpy, { passive: true });
    handleScrollSpy();

    return () => {
      window.removeEventListener('scroll', handleScrollSpy);
    };
  }, [currentPage]);

  const handleNav = (page, sectionId) => (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(page, sectionId);
    }
    setMobileMenuOpen(false);
    setIsScrollingDown(false);

    if (page === 'contact' || page === 'about' || page === 'services' || page === 'customer-form') {
      setActiveSection(page);
    } else if (sectionId) {
      setActiveSection(sectionId);
    } else {
      setActiveSection('home');
    }
  };

  // Determine visibility state
  const shouldShow = isVisible && (!isScrollingDown || isAtTop);

  return (
    <header className={`navbar-wrapper ${shouldShow ? 'navbar-visible' : 'navbar-hidden'}`}>
      <div className="navbar-container">
        {/* Left Side: Brand Logo */}
        <div className="nav-brand">
          <a href="/" className="brand-logo-link" onClick={handleNav('home')}>
            <img
              src={process.env.PUBLIC_URL + '/images/meena-logo.png'}
              alt="Meena Insurance & Investments"
              className="brand-logo-img"
            />
          </a>
        </div>

        {/* Right Side: Navigation Links (Home, About Us, Services, Contact) */}
        <nav className="nav-group nav-right">
          <a
            href="/"
            className={`nav-link ${activeSection === 'home' && currentPage === 'home' ? 'active' : ''}`}
            onClick={handleNav('home')}
          >
            Home
          </a>
          <a
            href="/about"
            className={`nav-link ${currentPage === 'about' ? 'active' : ''}`}
            onClick={handleNav('about')}
          >
            About Us
          </a>
          <a
            href="/services"
            className={`nav-link ${currentPage === 'services' || currentPage === 'life-insurance' ? 'active' : ''}`}
            onClick={handleNav('services')}
          >
            Services
          </a>
          <a
            href="/contact"
            className={`nav-link ${currentPage === 'contact' ? 'active' : ''}`}
            onClick={handleNav('contact')}
          >
            Contact
          </a>
        </nav>

        {/* Mobile Hamburger Toggle */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="mobile-menu-drawer">
          <a
            href="/"
            className={`mobile-nav-link ${activeSection === 'home' && currentPage === 'home' ? 'active' : ''}`}
            onClick={handleNav('home')}
          >
            Home
          </a>
          <a
            href="/about"
            className={`mobile-nav-link ${currentPage === 'about' ? 'active' : ''}`}
            onClick={handleNav('about')}
          >
            About Us
          </a>
          <a
            href="/services"
            className={`mobile-nav-link ${currentPage === 'services' || currentPage === 'life-insurance' ? 'active' : ''}`}
            onClick={handleNav('services')}
          >
            Services
          </a>
          <a
            href="/contact"
            className={`mobile-nav-link ${currentPage === 'contact' ? 'active' : ''}`}
            onClick={handleNav('contact')}
          >
            Contact
          </a>
          <a href="/contact" className="mobile-quote-btn" onClick={handleNav('contact')}>
            <span>Get a Quote</span>
            <ArrowRight size={17} className="btn-arrow" />
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
